import { environment } from '@/environments/environment';
import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Params } from '@angular/router';
import { catchError, map } from 'rxjs';
import { handleError } from '../tools/handle-error.tools';
import { Competencia } from '@/app/models/interface/competencia.model';
import {
  PaginatorModel,
  HasPagination,
} from '@/app/models/interface/paginator.model';
import { CustomResponse } from '@/app/models/interface/response.model';

@Injectable({ providedIn: 'root' })
export class CompetenciasService {
  private readonly http = inject(HttpClient);
  private readonly url = environment.api + 'competencias/';

  create(data: Partial<Competencia>) {
    return this.http.post<CustomResponse<Competencia>>(this.url, data).pipe(
      catchError((err) => handleError(err)),
      map((data) => {
        if (data.result && Array.isArray(data.result.capacitacionCompetencia)) {
          return {
            ...data.result,
            capacitaciones:
              data.result.capacitacionCompetencia?.map((d) => d.capacitacion) ||
              [],
            actividades:
              data.result.actividadCompetencia?.map((ac) => ac.actividad) || [],
          };
        }
        return data.result;
      }),
    );
  }

  /**
   * @description Find all records
   * @param params
   *              activo: boolean;
   *              id_visible: number;
   *              nombre: string;
   *              id_usuario: number;
   *              nombre_usuario: string; ()=> usuario de carga
   *              withRelations: boolean; ()=> trae las relaciones de la entidad
   */
  findAll(params?: Params) {
    return this.http
      .get<
        CustomResponse<PaginatorModel<Competencia[], HasPagination>>
      >(this.url, { params })
      .pipe(
        catchError((err) => handleError(err)),
        map((data) => {
          const d = data.result;
          if (d && Array.isArray(d.data)) {
            return {
              ...d,
              data: d.data.map((d) => ({
                ...d,
                capacitaciones:
                  d.capacitacionCompetencia?.map((m) => m.capacitacion) || [],
                actividades:
                  d.actividadCompetencia?.map((ac) => ac.actividad) || [],
              })),
            };
          }
          return d;
        }),
      );
  }

  findOne(id: string) {
    const url = this.url + id;
    return this.http.get<CustomResponse<Competencia>>(url).pipe(
      catchError((err) => handleError(err)),
      map((data) => {
        const d = data.result;
        if (d) {
          return {
            ...d,
            capacitaciones:
              d.capacitacionCompetencia?.map((m) => m.capacitacion) || [],
            actividades:
              d.actividadCompetencia?.map((ac) => ac.actividad) || [],
          };
        }
        return d;
      }),
    );
  }

  update(competencia: Partial<Competencia>) {
    const url = this.url + competencia.id;
    return this.http.put<CustomResponse<Competencia>>(url, competencia).pipe(
      catchError((err) => handleError(err)),
      map((data) => {
        const d = data.result;
        if (d) {
          return {
            ...d,
            capacitaciones:
              d.capacitacionCompetencia?.map((m) => m.capacitacion) || [],
            actividades:
              d.actividadCompetencia?.map((ac) => ac.actividad) || [],
          };
        }
        return d;
      }),
    );
  }

  changeState(state: boolean, id: string) {
    const url = this.url + id;
    return this.http.patch<CustomResponse<Competencia>>(url, { state }).pipe(
      catchError((err) => handleError(err)),
      map((data) => {
        const d = data.result;
        if (d) {
          return {
            ...d,
            capacitaciones:
              d.capacitacionCompetencia?.map((m) => m.capacitacion) || [],
            actividades:
              d.actividadCompetencia?.map((ac) => ac.actividad) || [],
          };
        }
        return d;
      }),
    );
  }
}
